import {
  normalizeIncomingExternalLinks,
  splitExternalLinksForSave,
} from 'api/linkedWorkItems/linkedWorkItems.helpers';
import { EMPTY_ARRAY, EMPTY_OBJECT } from 'constants/defaults';
import { intersection } from 'lodash-es';
import { DateTime } from 'luxon';
import { defaultState, defaultStateId } from 'redux/modules/hourStates.constants';
import type { HourState } from 'redux/modules/hourStates.schema';
import type { Changes } from 'redux/modules/hours.types';
import { ChangeKeys } from 'redux/modules/hours.types';
import type { TagsDictionary } from 'redux/modules/tags.types';
import type { Currency } from 'types/account';
import type { HourAnalyticsContext, HourAnalyticsSaveContext } from 'types/analytics';
import type { RawAudit, RawHourAuditChanges } from 'types/audits';
import { AuditActions } from 'types/audits';
import type {
  EnrichedHourDraft,
  Hour,
  HourAsCard,
  HourDraft,
  HourDraftStatusMessages,
  HourTimestamp,
  NormalizedHourToApi,
  ProjectJoinMode,
} from 'types/hour';
import { HourLockedReasons } from 'types/hour';
import type { LightProject, Project } from 'types/project';
import type { Tag, TagBase } from 'types/tag';
import type { MemoryGroup } from 'types/timeline';
import type { Duration } from 'types/units';
import type { User } from 'types/user';
import { calculateCost } from 'utils';
import { toStringWithOxfordCommas } from 'utils/array-helper';
import { isContrastReadable } from 'utils/colors';
import { calculateDuration, durationFromHHMMSS } from 'utils/durations';
import { getHourRateForHour, isHourLocked } from 'utils/hours';
import { getProjectColor } from 'utils/project';
import { stripMentionMarkup } from 'utils/text-helper';
import { calculateDurationFromTimestamps } from 'utils/timestamps';
import { notNull } from 'utils/typesafe';
import { combinedDurationOfMemories } from './timeline';

export interface HourDraftFactoryInitializer extends Omit<HourDraft, 'project'> {
  project: Project | number | LightProject | null;
}
export function hourDraftFactory(
  payload: Partial<HourDraftFactoryInitializer>,
  context: HourAnalyticsContext
): HourDraft {
  const timestamp = +new Date();
  const defaults: HourDraft = {
    id: null,
    suggestion_id: null,
    note: '',
    day: DateTime.now().toISODate(),
    cost: calculateCost(0),
    estimated_cost: calculateCost(0),
    internal_cost: calculateCost(0),
    profit: calculateCost(0),
    // when normalized, this will only reference the project id
    project: null,
    user: {
      id: null!,
    },
    duration: calculateDuration(0),
    estimated_duration: calculateDuration(0),
    sequence: 999,
    label_ids: [],
    entry_ids: [],
    external_links: [],
    timer_state: 'default',
    timer_started_on: 0,
    timer_stopped_on: 0,
    created_at: timestamp,
    updated_at: timestamp,
    hours: 0,
    minutes: 0,
    seconds: 0,
    to: null,
    from: null,
    timestamps: [],
    required_label_ids: [],
    billed: false,
    billable: true,
    user_ids: [],
    estimated: false,
    deleted: false,
    manage: true,
    locked: false,
    locked_reason: null,
    invoice_id: null,
    forecast_id: null,
    context,
    state: null,
    profitability: undefined,
  };

  const durationFromHMS = durationFromHHMMSS(
    payload.hours ?? 0,
    payload.minutes ?? 0,
    payload.seconds
  );
  // If entry has server-side values for time:
  if (durationFromHMS.total_seconds > 0) {
    defaults.duration = durationFromHMS;
  }

  if (payload.from && !payload.to) {
    const durationInSeconds = payload.duration?.total_seconds || durationFromHMS.total_seconds;
    payload.to = DateTime.fromISO(payload.from).plus({ seconds: durationInSeconds }).toISO();
  }

  const project =
    typeof payload.project === 'number' ? payload.project : (payload.project?.id ?? null);

  const timestamps: Array<HourTimestamp> = getTimestampsFromLegacyFields(payload);

  return {
    ...defaults,
    ...payload,
    project,
    timestamps,
    // Backfill the `timely://work-items/<id>` uri on native linked work entries that arrive
    // without one (deploy-skew protection; the backend emits it) so the picker's chip and
    // pool resolution work for them. Fully-formed links pass through untouched.
    external_links: normalizeIncomingExternalLinks(payload.external_links),
  };
}

export function getTimestampsFromLegacyFields(
  hour: Partial<Omit<HourDraft, 'project'>>
): Array<HourTimestamp> {
  const isOldFieldUsed = !!hour.from && !!hour.to;
  const isNewFieldEmpty = !hour.timestamps || hour.timestamps.length === 0;

  if (isOldFieldUsed && isNewFieldEmpty) {
    const from = hour.from!;
    const to = hour.to!;
    return [{ from, to, entry_ids: [] }];
  } else {
    return hour.timestamps || [];
  }
}

interface CardOptions {
  minHeightInPx?: number;
  isEditing?: boolean;
  isSelected?: boolean;
  isSelectionMode?: boolean;
  duration?: Duration;
  memoryGroups?: Array<MemoryGroup>;
  tags?: TagsDictionary;
  hasDurationFromTimestamps?: boolean;
  defaultCurrency: Currency;
  states: Record<number, HourState | undefined>;
  hasHourStatesFeature?: boolean;
  userRoleId?: number;
  userLedUserIds?: Set<number>;
}

export function hourAsCardFactory(
  hour: Omit<EnrichedHourDraft, 'context'>,
  options: CardOptions
): HourAsCard {
  const {
    isEditing = false,
    isSelected,
    isSelectionMode,
    memoryGroups = EMPTY_ARRAY,
    tags = EMPTY_OBJECT,
    defaultCurrency,
    hasHourStatesFeature = false,
    userRoleId = -1,
    states,
    userLedUserIds,
  } = options;

  // When duration is live-calculated from Memories, use that as basis instead
  const duration = options.duration || hour.duration;

  // Calculate duration to compare to logged duration
  let calculatedDuration = duration;

  if (memoryGroups.length > 0) {
    if (options.hasDurationFromTimestamps) {
      calculatedDuration = calculateDurationFromTimestamps(hour.timestamps || []);
    } else {
      calculatedDuration = combinedDurationOfMemories(
        memoryGroups.flatMap(memoryGroup => memoryGroup.memories)
      );
    }
  }

  // Let project decide if we should show monetary values
  // Mainly used in dirty states (ie, editing draft), ref ch8861
  const isBillable = hour.billable;
  const hourRate = hour.id && hour.hour_rate ? hour.hour_rate : getHourRateForHour(hour);
  const costPerSecond = hourRate / 3600;

  const numLoggedSecondsExact = duration.total_seconds ?? 0;
  const numLoggedMinutesRoundedDown = Math.floor(numLoggedSecondsExact / 60);
  const numLoggedSecondsRoundedDown = numLoggedMinutesRoundedDown * 60;

  // When calculating logged money we use seconds floored down to closest minute for each hour!
  const numLoggedMoney = isBillable ? costPerSecond * numLoggedSecondsRoundedDown : 0;

  // Poor, fast workaround for https://app.clubhouse.io/timelyapp/story/9077/stopping-timer-send-the-page-into-crash
  // This should be solved at the core, related to timers. The issue can be seen triggering @ https://www.useloom.com/share/9ab1665bc08c4999bae179d0e47970a1
  const numPlannedSeconds = hour.estimated_duration
    ? hour.estimated_duration.total_seconds || 0
    : 0;
  const numPlannedMoney = isBillable ? hour.estimated_cost.amount : 0;
  const isTimerRunning = hour.timer_state === 'start';
  const projectColor = `#${getProjectColor(hour.project?.base)}`;
  const isSuggestion = !!hour.suggestion_id && !hour.id;
  const showLogged = !isTimerRunning && (numLoggedSecondsExact > 0 || !numPlannedSeconds);
  const hasPlannedTime = !isTimerRunning && !!numPlannedSeconds;

  const loggedDurationMemoriesMismatch =
    !isTimerRunning && !hour.locked && duration.formatted !== calculatedDuration.formatted;

  const hourTags = hourTagsFactory(hour.label_ids, tags);
  const timestamps: Array<HourTimestamp> = getTimestampsFromLegacyFields(hour);

  let isBilled = false;
  let isLocked = false;
  let isInvoiced = false;

  if (!hasHourStatesFeature) {
    isBilled = hour.billed;
    isLocked = hour.locked;
    isInvoiced = hour.locked && hour.locked_reason === HourLockedReasons.Invoiced;
  } else if (hour.state) {
    const hourState = states?.[hour.state?.id ?? defaultStateId] ?? hour.state ?? defaultState;
    const userIsTeamLead = !!userLedUserIds?.has(hour.user.id);
    isBilled = hourState.billed;
    isLocked = isHourLocked(hourState, { states, userRoleId, userIsTeamLead });
    isInvoiced = hourState.name === 'Invoiced'; // TODO: Update when "old" system is gone
  }

  return {
    type: 'hour',
    id: hour.id!,
    user_id: hour.user.id,
    note: hour.note || '',
    day: hour.day,
    from: hour.from || null,
    to: hour.to || null,
    timestamps,
    sequence: hour.sequence,
    numLoggedSecondsExact,
    numLoggedSecondsRoundedDown,
    numPlannedSeconds,
    numLoggedMoney,
    numPlannedMoney,
    costPerSecond,
    isBilled,
    isBillable,
    isInvoiced,
    isForecast: !!hour.forecast_id,
    forecastId: hour.forecast_id,
    externalId: hour.external_id,
    isLocked,
    invoiceId: hour.invoice_id,
    isMemoryBased: hour.entry_ids.length > 0,
    isTimerRunning,
    timerRunningSince: hour.timer_started_on,
    loggedDurationMemoriesMismatch,
    numCalculatedSeconds: calculatedDuration.total_seconds,
    projectId: hour.project.base.id,
    projectName: hour.project.base.name,
    projectColor,
    projectCurrency: hour.project.fullProject?.currency ?? defaultCurrency,
    clientId: hour.project.base.client.id,
    clientName: hour.project.base.client.name,
    entryIds: hour.entry_ids,
    memoryGroups,
    manage: hour.manage,
    tags: hourTags,
    stateId: hour.state?.id ?? null,
    // expand on this as needed, will allow re-render of <Hour />
    // Project reference added as likely workaround for when hours load before projects
    cacheBuster: JSON.stringify({
      updated: hour.updated_at,
      project_id: hour.project.base.id,
      numLoggedMoney,
      isTimerRunning,
      hourTags,
      groups: memoryGroups.map(memoryGroup => ({
        id: memoryGroup.ref,
        expanded: memoryGroup.isExpanded,
        ids: memoryGroup.memories.map(memory => ({
          id: memory.id,
          expanded: memory.isExpanded,
        })),
      })),
    }),
    ui: {
      // Drafts that are unsaved & based on a suggestion should act as suggestion
      isSuggestion,
      isBasedOnSuggestion: !!hour.suggestion_id,
      isEditing,
      isSelected,
      isSelectionMode,
      hasPlannedTime,
      hasNoNote: !hour.note,
      showLogged,
      isContrastReadable: isContrastReadable('#fff', projectColor),
      hasHourStatesFeature,
    },
  };
}

export function resetHourStateProps<T extends Hour<number> | HourDraft>(hour: T): T {
  return {
    ...hour,
    state: null,
    billed: false,
    locked: false,
    locked_reason: null,
  };
}

export function normalizedHourDraftToXhrFactory(
  hour: Omit<HourDraft, 'context'>,
  saveContext: HourAnalyticsSaveContext,
  canUnlockHour = false,
  hasHourStatesFeature = false,
  hasBillingFeature = false,
  projectJoinMode?: ProjectJoinMode
): NormalizedHourToApi {
  // Native picks (synthetic `timely://work-items/<id>` uri) are sent as linked_work_item_ids;
  // only TIC picks travel via external_links. linked_work_item_ids has full-replace
  // semantics, so it is always sent — an empty array clears a previously linked native item.
  const { externalLinks, linkedWorkItemIds } = splitExternalLinksForSave(hour.external_links || []);
  const payload: NormalizedHourToApi = {
    day: hour.day,
    note: stripMentionMarkup(hour.note),
    timer_state: hour.timer_state,
    timer_started_on: hour.timer_started_on,
    timer_stopped_on: hour.timer_stopped_on,
    project_id: hour.project!,
    suggestion_id: hour.suggestion_id ?? undefined,
    forecast_id: hour.forecast_id ?? null,
    forecast_completed_at: hour.forecast_completed_at ?? undefined,
    label_ids: hour.label_ids || [],
    user_ids: hour.user_ids || [],
    entry_ids: hour.entry_ids || [],
    from: hour.from || null,
    to: hour.to || null,
    timestamps: hour.timestamps || null,
    hours: hour.duration?.hours,
    minutes: hour.duration?.minutes,
    seconds: hour.duration?.seconds,
    estimated_hours: hour.estimated_duration.hours,
    estimated_minutes: hour.estimated_duration.minutes,
    sequence: hour.sequence,
    billable: hour.billable,
    context: saveContext,
    state_id: (hour.state?.id ?? 0) === 0 ? null : hour.state?.id,
    billed: hour.billed,
    locked: hour.locked,
    locked_reason: hour.locked_reason,
    external_links: externalLinks,
    linked_work_item_ids: linkedWorkItemIds,
  };

  if (hour.id) {
    payload.id = hour.id;
  }

  if (hour.user?.id) {
    payload.user_id = hour.user.id;
  }

  // Only AutoSheet-created hours carry a proposal id; omit the field entirely otherwise.
  if (hour.autosheet_proposal_id) {
    payload.autosheet_proposal_id = hour.autosheet_proposal_id;
  }

  // Under ALL_PROJECTS_LOGGING, the user picks how to log to a non-member
  // project (join permanently vs log once). Omit the field entirely otherwise
  // so the backend keeps its default "add" behaviour (backward compatible).
  if (projectJoinMode) {
    payload.project_join_mode = projectJoinMode;
  }

  if (!hasHourStatesFeature && !hasBillingFeature) {
    if ((!hour.locked || hour._unlockedByUser) && canUnlockHour) {
      payload.locked = false;
      payload.locked_reason = null;
    } else if (hour.locked) {
      payload.locked = true;
      payload.locked_reason = hour.locked_reason;
    }
  }

  return payload;
}

/**
 * Rails rejects a CREATE that carries any `linked_work_item_ids` when the account lacks
 * `api_linked_work` (every id on a new hour is an addition), which would fail the whole request —
 * a bulk copy included. Such an account's new hours drop their native links instead, as they did
 * before native linked work existed; `[]` is always accepted. Updates pass through untouched:
 * echoing an hour's existing ids is always allowed, and dropping them would unlink the hour.
 * `isCreate` is the caller's to say — a bulk copy is a create whose payload still carries the
 * source hour's id.
 */
export function omitLinkedWorkItemIdsFromUnsupportedCreate(
  payload: NormalizedHourToApi,
  { isCreate, hasLinkedWorkItemsSupport }: { isCreate: boolean; hasLinkedWorkItemsSupport: boolean }
): NormalizedHourToApi {
  if (!isCreate || hasLinkedWorkItemsSupport || !payload.linked_work_item_ids?.length) {
    return payload;
  }
  return { ...payload, linked_work_item_ids: [] };
}

// who
export interface HourDraftStatusMessagesFactory {
  xhrError: string | null;
  isMissingRequiredTags: boolean;
  isMissingRequiredAnyTag: boolean;
  isActingAsOtherUser: boolean;
  draftOwner: User;
  isBasedOnSuggestion: boolean;
  draft: Pick<EnrichedHourDraft, 'id' | 'source' | 'project'>;
  isBilled: boolean;
  canEdit: boolean;
  isLocked: boolean;
  isDayLocked: boolean;
  canUnlockDays: boolean;
  requiredTags: Array<Pick<Tag, 'name'>>;
  isProjectAvailable: boolean;
  isMissingRequiredNote: boolean;
  isLoading: boolean;
  isInvoiced: boolean;
  didRemoveUnavailableTags?: boolean;
  didRemoveUnavailableUsers?: boolean;
  isMaybeChangedDueToPusherUpdate: boolean;
  hasHourStatesFeature: boolean;
  currentUser?: User;
  isExceedingSingleTagLimit?: boolean;
}

export const hourDraftStatusMessagesFactory = ({
  xhrError,
  isMissingRequiredTags,
  isMissingRequiredAnyTag,
  draft,
  isActingAsOtherUser,
  draftOwner,
  isBasedOnSuggestion,
  isBilled,
  canEdit,
  isLocked,
  isDayLocked,
  canUnlockDays,
  requiredTags,
  isProjectAvailable,
  isMissingRequiredNote,
  isLoading,
  isInvoiced,
  didRemoveUnavailableTags,
  didRemoveUnavailableUsers,
  isMaybeChangedDueToPusherUpdate,
  hasHourStatesFeature,
  currentUser,
  isExceedingSingleTagLimit,
}: Partial<HourDraftStatusMessagesFactory>): HourDraftStatusMessages => {
  const messages: HourDraftStatusMessages = [];
  let hasLockedMessage = false;

  if (xhrError) {
    messages.push({
      type: 'error',
      key: 'xhrError',
      title: `Something went wrong`,
      message: xhrError,
    });
  }

  if (isMissingRequiredNote) {
    messages.push({
      type: 'error',
      key: 'isMissingRequiredNote',
      title: `Note is required for this project`,
    });
  }

  if (isMissingRequiredTags) {
    const tagNames = requiredTags?.map(tag => tag.name) ?? [];
    messages.push({
      type: 'warning',
      key: 'isMissingRequiredTags',
      title: `Missing required tags`,
      icon: 'tag',
      message: `${toStringWithOxfordCommas(tagNames)}`,
    });
  }

  if (isMissingRequiredAnyTag) {
    messages.push({
      type: 'warning',
      key: 'isMissingRequiredAnyTag',
      title: 'Add at least one tag to your entry',
      icon: 'tag',
    });
  }

  if (isExceedingSingleTagLimit) {
    messages.push({
      type: 'error',
      key: 'isExceedingSingleTagLimit',
      title: 'Project allows at most one tag to be added.',
      icon: 'tag',
    });
  }

  if (isActingAsOtherUser && !xhrError && canEdit) {
    messages.push({
      type: 'info',
      key: 'actingAsOtherUser',
      title: `You're acting as ${draftOwner?.name}`,
      avatar: draftOwner?.avatar ? draftOwner?.avatar.medium_retina : null,
      message: 'Their projects and tags might be different than yours',
    });
  }

  if (isDayLocked) {
    messages.push({
      type: 'info',
      key: 'Locked',
      title: `The day is locked`,
      icon: 'lock',
      message: canUnlockDays
        ? `Unlock the day to make changes`
        : `Ask your manager to unlock the day to make changes to its entries`,
    });
    hasLockedMessage = true;
  }

  if (isLocked && !canEdit && isProjectAvailable) {
    messages.push({
      type: 'warning',
      key: 'notAuthorized',
      title: isActingAsOtherUser
        ? `You can't make changes as ${draftOwner?.name}`
        : `You're not allowed to make changes`,
      icon: 'lock',
      message: 'Talk to your workspace admin about your permissions',
    });
    hasLockedMessage = true;
  }

  if (isBasedOnSuggestion && !draft?.id && !xhrError) {
    messages.push({
      type: 'info',
      key: 'basedOnSuggestion',
      title: `Draft suggested by Timesheet Assistant`,
      message: `It is based on your previous work and logging patterns.`,
    });
  }

  if (isInvoiced) {
    messages.push({
      type: 'info',
      key: 'Invoiced',
      title: `Invoiced`,
      icon: 'lock',
      message: `You may not make changes to an invoiced entry`,
    });
    hasLockedMessage = true;
  }

  if (isMaybeChangedDueToPusherUpdate && canEdit && !isLocked) {
    messages.push({
      type: 'info',
      key: 'pusherUpdate',
      title: 'This entry has been updated since you started editing',
    });
  }

  /// isBilled
  /// there are two scenarios for locked, not permitted, and already Billed.
  /// there is another sc
  if (isBilled && !canEdit && !hasHourStatesFeature) {
    // Maybe this text should change when the file is unlocked.
    messages.push({
      type: 'info',
      key: 'Billed',
      title: `Billed`,
      icon: 'lock',
      message: `You may not make changes to a billed entry`,
    });
    hasLockedMessage = true;
  }

  if (isBilled && isLocked && canEdit && !hasHourStatesFeature) {
    // Maybe this text should change when the file is unlocked.
    messages.push({
      type: 'info',
      key: 'Billed',
      title: `This entry has been billed`,
      message: `This entry has been billed`,
      actions: [{ action: 'onUnlock', key: 'onUnlock', label: 'Unlock to make changes' }],
    });
    hasLockedMessage = true;
  }

  // If selected project isn't available for the draft's owner
  // don't allow changes by default. Own-entry edits on active projects
  // are always permitted (see getHourDraftProjectIsAvailable), so this
  // branch only triggers for archived projects or for hours owned by
  // another user on a project the current user isn't a member of.
  if (!isProjectAvailable && isLocked && !isBilled && !isLoading) {
    const isArchived = !draft?.project?.base.active;
    let message = '';
    if (currentUser?.admin && isArchived) {
      message = 'Restore the project to make changes';
    } else if (!isArchived) {
      // Active project — the user must be looking at another user's
      // entry on a project they are not a member of.
      message = 'You are not a member of this project, so you cannot edit this entry.';
    } else if (canEdit) {
      message = `You are not a member of this project, or the project has been archived.`;
    } else {
      message = `You are not a member of this project, or the project has been archived. Ask an Admin for further details.`;
    }
    messages.push({
      type: 'info',
      key: 'ProjectArchived',
      title: isArchived ? 'Project is archived' : `Project unavailable`,
      icon: 'lock',
      message,
      actions:
        !canEdit || !isArchived
          ? []
          : [{ action: 'onUnlock', key: 'onUnlock', label: 'Unlock to make changes' }],
    });
    hasLockedMessage = true;
  }

  if (isLocked && !hasLockedMessage) {
    messages.push({
      type: 'info',
      key: 'Locked',
      title: `This entry has been locked`,
      icon: 'lock',
      message: `You may not make changes to a locked entry`,
      actions: canEdit
        ? [{ action: 'onUnlock', key: 'onUnlock', label: 'Unlock to make changes' }]
        : undefined,
    });
  }

  if (didRemoveUnavailableTags) {
    messages.push({
      key: 'didRemoveUnavailableTags',
      title:
        'Some of the previously selected tags are unavailable in this project, and were removed from the draft.',
      type: 'warning',
      icon: 'tag',
      actions: [{ action: 'onHide', key: 'onHide', label: 'Hide' }],
    });
  }

  if (didRemoveUnavailableUsers) {
    messages.push({
      key: 'didRemoveUnavailableUsers',
      title:
        "Some of the previously selected users don't have access to this project, and were removed from the draft.",
      type: 'warning',
      icon: 'user',
      actions: [{ action: 'onHide', key: 'onHide', label: 'Hide' }],
    });
  }

  return messages;
};

export const hourHistoryFactory = (
  audit: RawAudit<RawHourAuditChanges>,
  projectEntities: Record<number, LightProject>
): RawAudit<Partial<Changes>> => {
  const changes: Partial<Changes> = {};

  const changeKeys = (Object.keys(audit.changes) as Array<keyof typeof audit.changes>) || [];

  if (audit.action !== AuditActions.CREATE) {
    changeKeys.forEach(k => {
      const [from, to] = audit.changes[k]!;

      if (k === 'project_id') {
        const [from, to] = audit.changes[k]!;
        const fromProject = projectEntities[from];
        const toProject = projectEntities[to];

        changes[ChangeKeys.project] = {
          from: fromProject,
          to: toProject,
        };
        return;
      }

      changes[k] = {
        from,
        to,
      } as any; // This should be safe
    });
  }

  const user = {
    ...audit.user,
    id: audit.user?.id || 0,
    name: audit.user?.name || '',
    avatar: audit.user?.avatar || null,
  };

  return {
    ...audit,
    user,
    changes,
  };
};

export const hourTagsFactory = (
  tagIds: Array<number> = [],
  tags: TagsDictionary = {}
): Array<TagBase> => {
  return tagIds
    .map(id => {
      const tag = tags[id];
      if (!tag) {
        return null;
      }
      const childTags = tag?.children;
      const hourContainsChildTags = childTags ? !!intersection(tagIds, childTags).length : false;
      // if the hour contains the label's children, we want to render those instead of the parent
      if (hourContainsChildTags) {
        return null;
      }
      return { name: tag.name, emoji: tag.emoji, id };
    })
    .filter(notNull);
};
